import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { detectLanguage } from '../i18n/languages';
import { getPreferences, setPreferences } from '../services/api';
import { useAuth } from './AuthContext';

const PreferencesContext = createContext(null);

const PREFERENCE_KEYS = {
    darkMode: { localStorage: 'darkMode', parse: v => JSON.parse(v), default: true },
    language: { localStorage: 'language', parse: v => v, default: detectLanguage() },
    gameMode: { localStorage: 'gameMode', parse: v => v, default: 'ranked' },
    gameDifficulty: { localStorage: 'gameDifficulty', parse: v => parseInt(v, 10), default: 1 },
    tutorialSeen: { localStorage: 'wordlabyrinth_tutorial_seen', parse: v => v === 'true', default: false },
    recoveryDismissedFor: { localStorage: 'recoveryDismissedFor', parse: v => v || null, default: null },
    showDistanceIndicator: { localStorage: 'showDistanceIndicator', parse: v => JSON.parse(v), default: true },
    // Guided onboarding (see docs/ONBOARDING_REDESIGN.md). onboardingStep is
    // 0 (gate) | 1 | 2 | 3 | 'done' | 'skipped'. For registered users the server
    // value (incl. the step-0 backfill for prior players) wins on login.
    onboardingStep: { localStorage: 'wl_onboarding_step', parse: v => (v === 'done' || v === 'skipped') ? v : (parseInt(v, 10) || 0), default: 0 },
    onboardingCompleted: { localStorage: 'wl_onboarding_completed', parse: v => v === 'true', default: false },
    highContrast: { localStorage: 'wl_high_contrast', parse: v => JSON.parse(v), default: false },
    feedbackAskedAt: { localStorage: 'wl_feedback_asked_at', parse: v => v || null, default: null },
    feedbackRatedAt: { localStorage: 'wl_feedback_rated_at', parse: v => v || null, default: null },
};

function readLocalPreferences() {
    const prefs = {};
    for (const [key, config] of Object.entries(PREFERENCE_KEYS)) {
        if (!config.localStorage) {
            prefs[key] = config.default;
            continue;
        }
        try {
            const raw = localStorage.getItem(config.localStorage);
            prefs[key] = raw !== null ? config.parse(raw) : config.default;
        } catch {
            prefs[key] = config.default;
        }
    }
    return prefs;
}

function writeLocalPreference(key, value) {
    const config = PREFERENCE_KEYS[key];
    if (!config?.localStorage) return;
    try {
        if (typeof value === 'boolean') {
            // tutorialSeen uses string 'true', darkMode uses JSON
            if (key === 'tutorialSeen') {
                localStorage.setItem(config.localStorage, value ? 'true' : '');
            } else {
                localStorage.setItem(config.localStorage, JSON.stringify(value));
            }
        } else {
            localStorage.setItem(config.localStorage, String(value));
        }
    } catch {
        // QuotaExceededError - ignore
    }
}

export function PreferencesProvider({ children }) {
    const { isAuthenticated } = useAuth();
    const [preferences, setPrefs] = useState(readLocalPreferences);

    // High-contrast theme is a body attribute so CSS can key off it like dark mode.
    useEffect(() => {
        document.body.setAttribute('data-contrast', preferences.highContrast ? 'high' : 'normal');
    }, [preferences.highContrast]);
    const [loaded, setLoaded] = useState(false);
    const debounceTimer = useRef(null);
    const pendingSync = useRef({});
    const authenticatedRef = useRef(isAuthenticated);
    authenticatedRef.current = isAuthenticated;

    const flushToServer = useCallback(() => {
        const toSync = { ...pendingSync.current };
        pendingSync.current = {};
        // Guests keep preferences in localStorage; signing in seeds the server from there.
        if (!authenticatedRef.current || Object.keys(toSync).length === 0) return;
        setPreferences(toSync).catch(() => {});
    }, []);

    const updatePreference = useCallback((key, value) => {
        if (!(key in PREFERENCE_KEYS)) return;

        setPrefs(prev => {
            if (prev[key] === value) return prev;
            return { ...prev, [key]: value };
        });

        writeLocalPreference(key, value);

        // Server sync — immediate for critical keys, debounced for others
        pendingSync.current[key] = value;
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        const IMMEDIATE_KEYS = ['recoveryDismissedFor'];
        if (IMMEDIATE_KEYS.includes(key)) {
            flushToServer();
        } else {
            debounceTimer.current = setTimeout(flushToServer, 500);
        }
    }, [flushToServer]);

    const updatePreferences = useCallback((obj) => {
        const updates = {};
        for (const [key, value] of Object.entries(obj)) {
            if (key in PREFERENCE_KEYS) {
                updates[key] = value;
                writeLocalPreference(key, value);
                pendingSync.current[key] = value;
            }
        }
        if (Object.keys(updates).length > 0) {
            setPrefs(prev => ({ ...prev, ...updates }));
            if (debounceTimer.current) clearTimeout(debounceTimer.current);
            debounceTimer.current = setTimeout(flushToServer, 500);
        }
    }, [flushToServer]);

    // Sync with server on auth change
    useEffect(() => {
        if (!isAuthenticated) {
            setLoaded(true);
            return;
        }

        let cancelled = false;

        getPreferences().then(response => {
            if (cancelled || !response.success) {
                setLoaded(true);
                return;
            }

            const serverPrefs = response.preferences || {};
            const localPrefs = readLocalPreferences();

            // Server wins for keys it has; fill gaps from localStorage
            const merged = { ...localPrefs };
            const toSeed = {};

            for (const key of Object.keys(PREFERENCE_KEYS)) {
                if (key in serverPrefs) {
                    merged[key] = serverPrefs[key];
                    writeLocalPreference(key, serverPrefs[key]);
                } else if (merged[key] !== PREFERENCE_KEYS[key].default) {
                    // Local has a non-default value the server doesn't know about
                    toSeed[key] = merged[key];
                }
            }

            setPrefs(merged);
            setLoaded(true);

            // Seed server with local values it doesn't have yet
            if (Object.keys(toSeed).length > 0) {
                setPreferences(toSeed).catch(() => {});
            }
        }).catch(() => {
            if (!cancelled) setLoaded(true);
        });

        return () => { cancelled = true; };
    }, [isAuthenticated]);

    // Flush pending changes on unmount
    useEffect(() => {
        return () => {
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current);
                flushToServer();
            }
        };
    }, [flushToServer]);

    return (
        <PreferencesContext.Provider value={{ preferences, updatePreference, updatePreferences, loaded }}>
            {children}
        </PreferencesContext.Provider>
    );
}

export function usePreferences() {
    const context = useContext(PreferencesContext);
    if (!context) {
        throw new Error('usePreferences must be used within PreferencesProvider');
    }
    return context;
}

export default PreferencesContext;
